import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { ActivityButton } from 'components/ActivityButton'
import { useDeleteSystemBannerById } from 'hooks/api/useDeleteSystemBannerById'
import { useSnackbar } from 'stores/useSnackbar'
import { type SystemBannerDTO } from 'types/api'
import { formatDateTime } from 'utils/util'

interface DeleteBannerModalProps {
  isModalOpen: boolean
  data?: SystemBannerDTO | null
  toggleIsOpen: () => void
}

export const DeleteBannerModal = ({
  isModalOpen,
  toggleIsOpen,
  data,
}: DeleteBannerModalProps) => {
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)

  const useDeleteBannerMutation = useDeleteSystemBannerById({
    onSuccess: () => {
      setSnackbarMessage(`Banner successfully deleted`, 'success')
      toggleIsOpen()
    },
    onError: () => {
      setSnackbarMessage(
        `An error has occured while deleting the Banner`,
        'error'
      )
      toggleIsOpen()
    },
  })

  const handleDelete = (id?: number | null) => {
    useDeleteBannerMutation.mutate({ id })
  }

  return (
    <Dialog
      open={isModalOpen}
      maxWidth={'xl'}
      fullWidth
      PaperProps={{ sx: { p: 0, maxWidth: '900px' } }}
      onClose={toggleIsOpen}
    >
      <DialogTitle bgcolor={grey[50]} sx={{ p: 0 }} className="border-b">
        <Box px={4} py={4}>
          <Typography variant="h3" fontWeight={'bold'}>
            Delete Banner
          </Typography>
          <Typography variant="body-1" color={'text.secondary'}>
            Banner Type: {data?.type}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 4, px: 4 }}>
        <Box paddingBottom={2} paddingTop={3}>
          <Typography variant="h4" fontSize={18} paddingBottom={2}>
            Are you sure you want to delete this Banner?
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            sm={6}
            container
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
          >
            <InputLabel
              sx={{ fontSize: 16, paddingTop: 2, fontWeight: 'normal' }}
            >
              Title
            </InputLabel>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              value={data?.title}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputLabel
              sx={{ fontSize: 16, paddingTop: 2, fontWeight: 'normal' }}
            >
              Banner Content
            </InputLabel>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              key={data?.id}
              fullWidth
              aria-label="TextareaAutosize"
              defaultValue={' '}
              margin="normal"
              value={data?.content}
              aria-readonly
              multiline={true}
              minRows={4}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            container
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
          >
            <InputLabel
              sx={{ fontSize: 16, paddingTop: 2, fontWeight: 'normal' }}
            >
              Start Date
            </InputLabel>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              value={formatDateTime(data?.startDate)}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            container
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
          >
            <InputLabel
              sx={{ fontSize: 16, paddingTop: 2, fontWeight: 'normal' }}
            >
              End Date
            </InputLabel>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              value={formatDateTime(data?.endDate)}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{ borderTop: `1px solid ${grey[300]}`, pt: 2, pb: 2, px: 4 }}
      >
        <Button variant="text" onClick={toggleIsOpen}>
          Cancel
        </Button>
        <ActivityButton
          variant="contained"
          onClick={() => handleDelete(data?.id)}
          active={useDeleteBannerMutation.isPending}
        >
          Delete Banner
        </ActivityButton>
      </DialogActions>
    </Dialog>
  )
}
